import React from 'react';
import { useTranslation } from 'react-i18next';
import { Meeting } from '../../api/MeetingsAPI';
import { BolderText } from '../BolderText/BolderText';
import { meetingTypeToTranslationKey } from '../../shared/meetingTypeToTranslationKey';
import { getMeetingBorderColor, getMeetingColor } from '../../shared/meetingTypeToColor';
import Pill from '../Pill/Pill';
import Tooltip from '../Tooltip/Tooltip';
import { parseDateTime } from '../../shared/timeUtils';
import { Clock, Mail, Phone, Calendar, FileText } from 'lucide-react';

interface MeetingSearchResultProps {
  meeting: Meeting;
  onView: (meeting: Meeting) => void;
  timezone?: string;
}

export default function MeetingSearchResult({
  meeting,
  onView,
  timezone = 'UTC'
}: MeetingSearchResultProps) {
  const { t } = useTranslation();

  const handleClick = () => {
    onView(meeting);
  };

  const startDateTime = parseDateTime(meeting.startTime, timezone);
  const endDateTime = parseDateTime(meeting.endTime, timezone);
  const displayName = `${meeting.customerName} ${meeting.customerSurname || ''}`.trim();
  const meetingTypeColor = getMeetingColor(meeting.type);
  const meetingBorderColor = getMeetingBorderColor(meeting.type);

  return (
    <div 
      className="group p-3 sm:p-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      tabIndex={0}
      role="button"
      aria-label={t("calendar.search_result.view_meeting_aria", { customerName: displayName, meetingType: t(meetingTypeToTranslationKey(meeting.type)) }) as string}
    >
      <div className="flex flex-col space-y-2 sm:space-y-3">
        {/* Main Content */}
        <div className="flex-1 min-w-0">
          {/* Header Row */}
          <div className="flex items-center space-x-3 mb-2">
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-semibold text-gray-900 truncate">
                {displayName}
              </h4>
              {/* Contact Information Group */}
              <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-4 text-xs text-gray-600 mt-1">
                {meeting.email && (
                  <span className="flex items-center space-x-1">
                    <Mail className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">{meeting.email}</span>
                  </span>
                )}
                {meeting.phone && (
                  <span className="flex items-center space-x-1">
                    <Phone className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">{meeting.phone}</span>
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Pill 
                text={t(meetingTypeToTranslationKey(meeting.type))} 
                color="grey"
                customColor={{
                  backgroundColor: meetingTypeColor,
                  borderColor: meetingBorderColor,
                  textColor: '#000'
                }}
              />
            </div>
          </div>

          {/* Details Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-gray-600">
            <div className="flex items-center space-x-2 min-w-0">
              <Calendar className="w-3 h-3 text-gray-400 flex-shrink-0" />
              <span className="font-medium">{t("calendar.search_result.date")}:</span>
              <BolderText text={startDateTime.date} />
            </div>
            <div className="flex items-center space-x-2 min-w-0">
              <Clock className="w-3 h-3 text-gray-400 flex-shrink-0" />
              <span className="font-medium">{t("calendar.search_result.time")}:</span>
              <span className="truncate">
                {startDateTime.time} - {endDateTime.time}
              </span>
            </div>
          </div>

          {/* Wedding Date */}
          {meeting.weddingDate && (
            <div className="mt-2 text-xs text-gray-600">
              <span className="font-medium">{t("calendar.search_result.wedding_date")}: </span>
              <BolderText text={parseDateTime(meeting.weddingDate, timezone).date} />
            </div>
          )}

          {/* Notes - Bottom with crop limit */}
          {meeting.notes && (
            <div className="mt-2 text-xs text-gray-600">
              <div className="flex items-start space-x-2">
                <FileText className="w-3 h-3 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <span className="font-medium">{t("calendar.search_result.notes")}: </span>
                  <Tooltip content={meeting.notes}>
                    <span className="block break-words">
                      {meeting.notes.length > 40 ? `${meeting.notes.substring(0, 40)}...` : meeting.notes}
                    </span>
                  </Tooltip>
                </div>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
